import { Fragment, useState } from "react";

import classnames from "classnames";
import { isNotEmpty } from "neetocist";
import { Down, Up } from "neetoicons";
import { Button, Typography } from "neetoui";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import Block from "./Block";
import { DEFAULT_TRUNCATE_AT, STYLES } from "./constants";

const DetailsBlock = ({
  title,
  actionBlock,
  details = [],
  className,
  style = STYLES.inline,
  truncate = false,
  truncateAt = DEFAULT_TRUNCATE_AT,
  children,
}) => {
  const [showTruncated, setShowTruncated] = useState(false);

  const { t } = useTranslation();

  const [visibleDetails, truncatedDetails] = truncate
    ? [details.slice(0, truncateAt), details.slice(truncateAt)]
    : [details];

  const renderDetailsValue = value => {
    if (typeof value === "string") {
      return (
        <Typography className="break-anywhere" style="body2">
          {value}
        </Typography>
      );
    }

    return <span className="min-w-0 flex-grow">{value}</span>;
  };

  return (
    <Block
      {...{ actionBlock, className }}
      title={title ?? t("neetoMolecules.metadata.detailsTitle")}
    >
      <div
        className="flex flex-col gap-y-3"
        data-testid="neeto-molecules-metadata-details-block"
      >
        {visibleDetails.map(({ label, value, type }, index) => (
          <Fragment key={index}>
            {type === "divider" ? (
              <hr className="neeto-ui-border-gray-200" />
            ) : (
              <div
                data-testid="details-card-detail-wrapper"
                className={classnames("flex", {
                  "items-center gap-x-4": style === STYLES.inline,
                  "flex-col": style === STYLES.multiline,
                })}
              >
                <Typography className="neeto-ui-text-gray-600" style="body2">
                  {label}
                </Typography>
                {renderDetailsValue(value)}
              </div>
            )}
          </Fragment>
        ))}
        {!truncate && children}
        {showTruncated && (
          <>
            {truncatedDetails.map(({ label, value, type }, index) => (
              <Fragment key={index}>
                {type === "divider" ? (
                  <hr className="neeto-ui-border-gray-200" />
                ) : (
                  <div
                    className={classnames("flex", {
                      "gap-x-4": style === STYLES.inline,
                      "flex-col": style === STYLES.multiline,
                    })}
                  >
                    <Typography
                      className="neeto-ui-text-gray-600"
                      style="body2"
                    >
                      {label}
                    </Typography>
                    {renderDetailsValue(value)}
                  </div>
                )}
              </Fragment>
            ))}
            {children}
          </>
        )}
        {truncate && isNotEmpty(truncatedDetails) && (
          <Button
            data-testid="expand-button"
            icon={showTruncated ? Up : Down}
            style="link"
            label={
              showTruncated
                ? t("neetoMolecules.metadata.card.viewLess")
                : t("neetoMolecules.metadata.card.viewMore")
            }
            onClick={() => setShowTruncated(showTruncated => !showTruncated)}
          />
        )}
      </div>
    </Block>
  );
};

DetailsBlock.propTypes = {
  /**
   * To specify the content to be rendered on the right side of the title.
   */
  actionBlock: PropTypes.node,
  /**
   * To specify the title of the block.
   */
  title: PropTypes.string,
  /**
   * The details to be rendered inside the block.
   */
  details: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.node,
      type: PropTypes.string,
    })
  ),
  /**
   * To specify whether the label and value should be rendered in a single line or in two lines.
   */
  style: PropTypes.oneOf(["inline", "multiline"]),
  /**
   * To specify whether the visible details should be truncated at a specific point.
   */
  truncate: PropTypes.bool,
  /**
   * To specify the position at which the details should be truncated and `View more` button should be shown.
   */
  truncateAt: PropTypes.number,
  /**
   * To specify external classNames as overrides to the `DetailsBlock` component.
   */
  className: PropTypes.string,
  /**
   * The additional content to be rendered inside the block.
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

export default DetailsBlock;
